/**
 * draftToArtifact — bridge a saved DraftDocument into the AI chat's artifact
 * shape, so any document in My Documents can be opened (and revised) in the
 * conversation.
 *
 * The chat follows the ChatGPT/Claude attach pattern: the artifact panel holds
 * the rich state (full HTML), while the wire history carries only a plain-text
 * trace (buildWireHistory) that the backend uses as the AI's revision baseline.
 * This function produces the panel-side object; everything downstream already
 * works because it's the same shape drafting tools emit.
 */

import { htmlToPlainText } from './draftUtils';

// Inverse of AgentPage's DRAFT_TYPE_MAP (artifact kind → documentType).
const TYPE_TO_KIND = {
  resolution: 'resolution',
  minutes: 'minutes',
  document: 'document',
  report: 'document', // reports revise like generic documents in chat
};

const fmtDate = (iso) => {
  const t = iso ? new Date(iso) : null;
  return t && !Number.isNaN(t.getTime())
    ? t.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;
};

const SOURCE_LABEL = {
  ai: 'AI-generated',
  fallback: 'template',
  preview: 'template preview',
  'user-edited': 'user-edited',
};

/**
 * One factual sentence about the document's provenance. It rides in the seeded
 * assistant message CONTENT, so it enters the wire history and the model can
 * answer "who created this, for whom, and when?" from stated fact — never by
 * inventing provenance it doesn't have.
 */
export function draftMetaLine(doc) {
  const parts = [];
  const type = TYPE_TO_KIND[doc.documentType] || 'document';
  const co = doc.companyName || doc.companyCin;
  parts.push(`a ${type}${co ? ` for ${co}` : ''}`);
  if (doc.createdByName) parts.push(`created by ${doc.createdByName}`);
  const created = fmtDate(doc.createdAt);
  if (created) parts.push(`on ${created}`);
  const updated = fmtDate(doc.updatedAt);
  if (updated && updated !== created) parts.push(`last updated ${updated}`);
  const src = SOURCE_LABEL[doc.docSource];
  if (src) parts.push(`origin: ${src}`);
  return parts.join(', ');
}

/**
 * @param {object} doc  A full DraftDocument (draftsAPI.getById/getByRef result)
 * @returns the chat artifact, or null when the doc has no usable content.
 */
export function draftDocToArtifact(doc) {
  const html = doc && doc.docHtml;
  if (!html) return null;
  return {
    kind: TYPE_TO_KIND[doc.documentType] || 'document',
    title: doc.documentTitle || 'Saved document',
    html,
    plainText: htmlToPlainText(html) || '',
    source: 'user-edited', // library docs are the user's content, never "AI-generated"
    companyCin: String(doc.companyCin || '').toUpperCase() || null,
    // Library provenance — drives the "From My Documents" chip and the
    // Update-original save path. Doc-shaped payloads WITHOUT an _id (e.g. an
    // unsaved preview expanded from a draft modal) label as template content.
    draftId: doc._id || null,
    refNumber: doc.refNumber || null,
    // T7 — the platform catalog id this document came from, when the handoff
    // carried one (the draft modals' "Open in AI Chat"). It rides the wire draft
    // trace (chat/wire.js) so the backend's draft_resolution tool can resolve the
    // statutory class from the shared id→kind table instead of guessing from the
    // topic wording. Saved library docs have no catalog id — omitted, not faked.
    ...(doc.catalogId ? { catalogId: String(doc.catalogId) } : {}),
    fromLibrary: !!doc._id,
    ...(doc._id ? {} : { fromTemplate: true }),
    metaLine: draftMetaLine(doc),
  };
}

export default draftDocToArtifact;
